import { Box } from '@mui/material';
import React from 'react';
import { blockGridColumns, type FieldBlock } from '../../domain/setup/fieldTypes';
import { LIVE_HIDDEN_KEYS } from '../../domain/setup/liveSetupRules';
import { SectionCard } from '../../ui/primitives';
import type { LiveSetupStepProps } from './liveSetupStepProps';
import SetupFieldInput from './SetupFieldInput';
import SetupLogoCard from './SetupLogoCard';
import StaffContactPicker from './StaffContactPicker';

const CHECK_ON_CHANGE = new Set(['select', 'date', 'colour', 'staff']);

type Props = LiveSetupStepProps & { block: FieldBlock; errors: Record<string, string> };

/**
 * One block of a live setup step: its fields, plus the controls that act on the server rather
 * than through autosave (the account contact picker, and the fund logo ahead of the colours).
 */
export default function LiveSetupBlock(props: Props) {
  const { block, errors, setup, setupId, section, values, readOnly, busy, session } = props;
  const { change, blur, action } = props;
  const fields = block.fields.filter(
    (field) => !LIVE_HIDDEN_KEYS.has(field.key) && (!field.showIf || field.showIf(values)),
  );
  if (!fields.length) return null;
  return (
    <>
      {section.id === 'branding' && block.title === 'Colours' && (
        <SetupLogoCard
          setup={setup}
          readOnly={readOnly}
          busy={busy}
          upload={(file) => action(() => session.api.uploadSetupLogo(setupId, file))}
          useDefault={() => change('brand.logoUrl', '')}
        />
      )}
      <SectionCard title={block.title} subtitle={block.description}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: blockGridColumns(block.columns) }}>
          {block.title === 'Account contact' && (
            <StaffContactPicker
              api={session.api}
              email={String(values['account.email'] ?? '')}
              disabled={readOnly || busy}
              choose={(staffId) => action(() => session.api.chooseAccountContact(setupId, staffId))}
            />
          )}
          {fields.map((field) => (
            <SetupFieldInput
              key={field.key}
              field={field}
              value={values[field.key]}
              prefill={setup.prefill[field.key]}
              readOnly={readOnly}
              error={errors[field.key]}
              onChange={(value) => {
                change(field.key, value);
                if (CHECK_ON_CHANGE.has(field.kind)) blur(field.key);
              }}
              onBlur={() => blur(field.key)}
            />
          ))}
        </Box>
      </SectionCard>
    </>
  );
}
